import React, { FC, memo, useCallback, useContext, useEffect, useRef } from "react";
import { useEffectOnce } from "@swiggy-private/core-ui";

// types
import { GlobalOverlayPriority, HalfcardIds, OverlayType, ReinforcementComms } from "@swiggy-private/cart-types";

// DLS
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// styles
import { BenefitsImage, CtaContainer } from "./styles";

// components
import CloseIcon from "../CloseIcon/CloseIcon";

// hooks
import { useImagePrefetch, PrefetchStates, CartOverlaysContext } from "@swiggy-private/checkout-core";

// constants
import { DATA_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { FTUE_HALFCARD_NR_EVENT } from "@super-ui/components/black-membership-widgets/constants";

interface FtuBottomsheetProps extends ReinforcementComms {
    analyticsEvents: {
        onImpressionFtueHalfcard: () => void;
        onClickFtueCta: () => void;
        onClickFtueClose: () => void;
    };
}

/* eslint-disable @typescript-eslint/naming-convention */
const FtuBottomsheet: FC<FtuBottomsheetProps> = ({ logo_ctx: logoCtx, analyticsEvents }) => {
    const [isOpen, setIsOpen] = React.useState(true);

    const { onImpressionFtueHalfcard, onClickFtueCta, onClickFtueClose } = analyticsEvents;

    const benefitsImage = [{ cloudinaryId: logoCtx?.logo || "", options: {} }];
    const { loadingState, startPrefetching } = useImagePrefetch(benefitsImage, FTUE_HALFCARD_NR_EVENT);
    const { enabledOverlayId, registerOverlays, onCloseOverlay } = useContext(CartOverlaysContext);
    const registerFtueBottomsheet = useRef(false);

    useEffectOnce(() => {
        onImpressionFtueHalfcard();
        startPrefetching();
    });

    useEffect(() => {
        if (!registerFtueBottomsheet.current) {
            registerOverlays({
                id: HalfcardIds.FTUE_HALFCARD,
                priority: GlobalOverlayPriority.M2C_FTUE,
                overlayType: OverlayType.HALFCARD,
            });
            registerFtueBottomsheet.current = true;
        }
    }, [registerOverlays]);

    const closeFtueBottomsheet = useCallback(() => {
        setIsOpen(false);
        onCloseOverlay();
    }, [onCloseOverlay]);

    const closeFtuBottomsheet = useCallback(() => {
        closeFtueBottomsheet();
        onClickFtueClose();
    }, [closeFtueBottomsheet, onClickFtueClose]);

    const onCtaClick = useCallback(() => {
        closeFtueBottomsheet();
        onClickFtueCta();
    }, [closeFtueBottomsheet, onClickFtueCta]);

    if (loadingState !== PrefetchStates.SUCCESS || enabledOverlayId !== HalfcardIds.FTUE_HALFCARD) {
        return null;
    }

    return (
        <HalfCard
            data-testid={DATA_TEST_IDS.FTUE_BOTTOMSHEET}
            isOpen={isOpen}
            onClose={closeFtuBottomsheet}
            overflowY="visible"
            hideCloseIcon
        >
            <CloseIcon
                dataTestId={DATA_TEST_IDS.FTUE_BOTTOMSHEET_CLOSE_BUTTON}
                onClose={closeFtuBottomsheet}
                isFtuBottomsheet={true}
            />
            <BenefitsImage data-testid={DATA_TEST_IDS.FTUE_BOTTOMSHEET_BENEFITS_IMAGE}>
                <Image cloudinaryId={logoCtx.logo} alt={logoCtx.text} />
            </BenefitsImage>
            <CtaContainer
                data-testid={DATA_TEST_IDS.FTUE_BOTTOMSHEET_CTA_CONTAINER}
                onClick={onCtaClick}
            ></CtaContainer>
        </HalfCard>
    );
};

export default memo(FtuBottomsheet);
